import { useMemo, useRef, useEffect, useState } from 'react';
import { selectLag } from './strategyLagSelect';
import { STRATEGY_CONFIGS, getStrategyScore } from './strategyConfigs';
import { parseDateMs } from './chartDateUtils';
import styles from './MarketHealth.module.css';

/**
 * Compact sparkline rendered as an inline SVG polyline. Draws the given data
 * points as a tiny line chart (no axes, no labels) — just the shape — scaled
 * to a FIXED value domain [domainMin, domainMax] so the height reads
 * consistently against the strategy's risk axis (not auto-fit to the local
 * min/max). Missing points (null) break the line into gaps.
 *
 * A faint dashed line marks the risk threshold at its position on the fixed
 * domain, so each preview shows at a glance where the score sits relative to
 * its trigger — mirroring the full Score History / Risk Pressure treatment.
 *
 * @param {{ data: (number|null)[], color: string, domainMin: number,
 *           domainMax: number, riskBoundary?: number, width?: number,
 *           height?: number }}
 */
const MiniSparkline = ({ data, color, domainMin, domainMax, riskBoundary, width = 120, height = 30 }) => {
  if (!data || data.length < 2) return null;

  const span = (domainMax - domainMin) || 1;
  const y = (v) => height - ((Math.max(domainMin, Math.min(domainMax, v)) - domainMin) / span) * height;
  const x = (i) => (i / (data.length - 1)) * width;

  // Subtle threshold line, drawn only when the boundary falls within the
  // fixed domain (it always does for the configured domains, but guard anyway).
  const showThreshold = typeof riskBoundary === 'number' && Number.isFinite(riskBoundary)
    && riskBoundary >= domainMin && riskBoundary <= domainMax;
  const thresholdY = showThreshold ? y(riskBoundary) : null;

  // Build polyline segments, breaking at null points so gaps show.
  const segments = [];
  let currentSegment = [];
  data.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) {
      currentSegment.push(`${x(i)},${y(v)}`);
    } else if (currentSegment.length) {
      segments.push(currentSegment);
      currentSegment = [];
    }
  });
  if (currentSegment.length) segments.push(currentSegment);

  if (segments.length === 0) return null;

  const gradientId = `sparkGrad-${color.replace('#', '')}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      className={styles.heartbeatSparkline}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {showThreshold && (
        <line
          x1="0"
          y1={thresholdY}
          x2={width}
          y2={thresholdY}
          stroke="#adb5bd"
          strokeWidth={1}
          strokeDasharray="2 2"
          strokeOpacity={0.5}
        />
      )}
      {segments.map((seg, i) => {
        const pts = seg.join(' ');
        const first = seg[0].split(',')[0];
        const last = seg[seg.length - 1].split(',')[0];
        const areaPoints = `${pts} ${last},${height} ${first},${height}`;
        return (
          <g key={i}>
            <polygon points={areaPoints} fill={`url(#${gradientId})`} />
            <polyline
              points={pts}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        );
      })}
    </svg>
  );
};

// The 4 strategies displayed in the heartbeat strip, in display order.
// `key` matches the backend mode name / signals key; `label` is the UI name.
// The sparkline value for each strategy comes from getStrategyScore (the
// canonical risk score), plotted on the strategy's fixed config domain.
const HEARTBEAT_MODES = [
  { key: 'adaptiveBalanced', label: 'Adaptive' },
  { key: 'balanced', label: 'Balanced' },
  { key: 'adaptive', label: 'Yield Curve' },
  { key: 'conservative', label: 'Conservative' },
];

// Must match DEFAULT_LAG in MarketHealth.js (true for all strategies).
const HEARTBEAT_DEFAULT_LAG = true;

const SPARKLINE_POINTS = 30;

const COLOR_INVESTED = '#28a745';
const COLOR_CASH = '#dc3545';
// Neutral gray when a strategy's current position is unknown (missing signal).
// A missing position is NOT the same as Cash, so it must not read red.
const COLOR_NEUTRAL = '#adb5bd';

// Excludes malformed/undated entries and sorts oldest→newest by date so the
// sparkline reads left (oldest) to right (newest) regardless of payload order.
const sortDatedAscending = (history) =>
  (history || [])
    .filter((d) => d && typeof d.date === 'string' && !Number.isNaN(parseDateMs(d.date)))
    .map((d) => ({ ...d, ts: parseDateMs(d.date) }))
    .sort((a, b) => a.ts - b.ts);

/**
 * Heartbeat status strip — 4 mini sparkline cards showing all strategies at a
 * glance. Sits at the top of the Market Health page, always visible.
 *
 * Each card shows a tiny 30-point sparkline of the strategy's canonical risk
 * score (higher = more risk), scaled to that strategy's fixed axis domain, plus
 * a status dot colored by the current Invested/Cash position (green = invested,
 * red = cash, gray = unknown). The score and the position are independent
 * signals: the sparkline never implies a position, and a missing position is
 * neutral, never Cash.
 *
 * Clicking a card selects that strategy in the main mode selector.
 */
const HeartbeatStrip = ({ history, signals, mode, onSelectMode }) => {
  // Track previous positions to detect state changes for the pulse animation.
  const prevPositions = useRef({});
  const [pulsing, setPulsing] = useState({});

  // Derive sparkline data and current position for each strategy.
  const cards = useMemo(() => {
    if (!history || history.length === 0) return [];

    // Oldest→newest, then take the latest SPARKLINE_POINTS entries.
    const ascending = sortDatedAscending(history);
    const recentSlice = ascending.slice(-SPARKLINE_POINTS);

    return HEARTBEAT_MODES.map((hm) => {
      const config = STRATEGY_CONFIGS[hm.key];
      // Canonical risk score per entry (null → gap in the sparkline).
      const sparkData = recentSlice.map((entry) => {
        const v = getStrategyScore(entry, hm.key);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      });

      // Current position: read from signals with the strategy's default lag.
      const signal = selectLag(signals?.[hm.key], HEARTBEAT_DEFAULT_LAG);
      const position = signal?.position; // 'invested' | 'cash' | undefined
      const isInvested = position === 'invested';
      const isCash = position === 'cash';
      const color = isInvested ? COLOR_INVESTED : isCash ? COLOR_CASH : COLOR_NEUTRAL;
      const statusLabel = isInvested ? 'Invested' : isCash ? 'Cash' : '—';

      return {
        ...hm,
        sparkData,
        domainMin: config.scoreDomain[0],
        domainMax: config.scoreDomain[1],
        riskBoundary: config.riskBoundary,
        position,
        isInvested,
        statusLabel,
        color,
      };
    });
  }, [history, signals]);

  // Detect state changes and trigger pulse animation.
  useEffect(() => {
    const newPulsing = {};
    for (const card of cards) {
      const prev = prevPositions.current[card.key];
      if (prev != null && prev !== card.position && card.position != null) {
        newPulsing[card.key] = true;
      }
    }

    const nextPositions = {};
    for (const card of cards) {
      nextPositions[card.key] = card.position;
    }
    prevPositions.current = nextPositions;

    if (Object.keys(newPulsing).length > 0) {
      setPulsing(newPulsing);
      const timer = setTimeout(() => setPulsing({}), 2000);
      return () => clearTimeout(timer);
    }
  }, [cards]);

  if (cards.length === 0) return null;

  return (
    <div className={styles.heartbeatStrip} role="group" aria-label="Strategy overview">
      {cards.map((card) => {
        const isActive = mode === card.key;
        const cardClass = [
          styles.heartbeatCard,
          isActive ? styles.heartbeatCardActive : '',
          pulsing[card.key] ? styles.heartbeatPulse : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={card.key}
            type="button"
            className={cardClass}
            onClick={() => onSelectMode(card.key)}
            aria-pressed={isActive}
            aria-label={`${card.label} strategy: ${card.statusLabel}`}
          >
            <div className={styles.heartbeatName}>{card.label}</div>
            <MiniSparkline
              data={card.sparkData}
              color={card.color}
              domainMin={card.domainMin}
              domainMax={card.domainMax}
              riskBoundary={card.riskBoundary}
              width={120}
              height={30}
            />
            <div className={styles.heartbeatStatus}>
              <span
                className={styles.heartbeatDot}
                style={{ backgroundColor: card.color }}
                aria-hidden="true"
              />
              <span>{card.statusLabel}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default HeartbeatStrip;
